import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/constants";
import { CheckCircle2, XCircle, RotateCcw, Play, Zap } from "lucide-react";

export default function ApprovalsPanel({ contractId, contract, currentUser, onChange }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const approvals = contract?.approvals || [];
  const templateName = contract?.workflow_template_name;

  const canDecide = (step) => {
    if (!currentUser) return false;
    if (step.status !== "pending") return false;
    const owned = new Set([currentUser.role, ...(currentUser.roles || [])]);
    return owned.has(step.role) || currentUser.role === "it_admin";
  };

  const decide = async (stepId, decision) => {
    if ((decision === "reject" || decision === "return") && !reason.trim()) {
      toast.error(`A reason is required to ${decision}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/contracts/${contractId}/approvals/${stepId}/decide`,
                     { decision, reason: reason.trim() });
      toast.success(`Step ${decision}d`);
      setReason("");
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Decision failed"); }
    finally { setBusy(false); }
  };

  const instantiate = async () => {
    setBusy(true);
    try {
      await api.post(`/contracts/${contractId}/workflow/instantiate`);
      toast.success("Workflow instantiated");
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (approvals.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="approvals-empty">
        <div className="text-sm font-bold text-slate-900 mb-1">No approval chain instantiated</div>
        <p className="text-xs text-slate-500 mb-4">Kick off the workflow to route this contract through the correct approvers.</p>
        <button data-testid="approvals-instantiate-btn" onClick={instantiate} disabled={busy}
                className="h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
          <Play className="w-4 h-4"/> Instantiate workflow
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="approvals-panel">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-[#005289]"/>
        <div className="text-sm font-bold text-slate-900">Approval Chain</div>
        {templateName && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{templateName}</span>}
      </div>

      <div className="space-y-2">
        {approvals.map((s, i) => (
          <div key={s.step_id} className={`border rounded p-3 ${
            s.status === "pending" ? "border-[#005289] bg-[#005289]/5" :
            s.status === "approved" ? "border-emerald-200 bg-emerald-50" :
            s.status === "rejected" ? "border-red-200 bg-red-50" :
            s.status === "returned" ? "border-amber-200 bg-amber-50" :
            "border-slate-200 bg-slate-50"
          }`} data-testid={`approval-step-${s.step_id}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-slate-500">#{i + 1}</span>
              <span className="font-semibold text-sm">{s.name}</span>
              <span className="text-[10px] text-slate-500">· {ROLE_LABELS[s.role] || s.role}</span>
              <StatusPill status={s.status}/>
              {s.decided_by_name && <span className="ml-auto text-[10px] text-slate-500">{s.status} by {s.decided_by_name}</span>}
            </div>
            {s.reason && <div className="text-xs mt-1 text-slate-600 italic">"{s.reason}"</div>}
            {canDecide(s) && (
              <div className="mt-3 space-y-2" data-testid={`approval-actions-${s.step_id}`}>
                <input value={reason} onChange={e => setReason(e.target.value)}
                       placeholder="Reason (required for reject/return)"
                       className="w-full h-8 px-2 border border-slate-200 rounded text-xs"
                       data-testid={`approval-reason-${s.step_id}`}/>
                <div className="flex gap-2">
                  <button disabled={busy} onClick={() => decide(s.step_id, "approve")} data-testid={`approve-${s.step_id}`}
                          className="h-8 px-3 rounded bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-60">
                    <CheckCircle2 className="w-3 h-3"/> Approve
                  </button>
                  <button disabled={busy} onClick={() => decide(s.step_id, "return")} data-testid={`return-${s.step_id}`}
                          className="h-8 px-3 rounded bg-amber-500 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-60">
                    <RotateCcw className="w-3 h-3"/> Return
                  </button>
                  <button disabled={busy} onClick={() => decide(s.step_id, "reject")} data-testid={`reject-${s.step_id}`}
                          className="h-8 px-3 rounded bg-red-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-60">
                    <XCircle className="w-3 h-3"/> Reject
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-[#005289] text-white",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800",
    returned: "bg-amber-100 text-amber-800",
    waiting: "bg-slate-100 text-slate-600",
    skipped: "bg-slate-100 text-slate-400 line-through",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${map[status] || "bg-slate-100"}`}>{status}</span>;
}
